import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { IMAGES, CONTACTS, STATS } from "@/data/content";

const PILLARS = [
    {
        name: "Marwaha Enterprises",
        text: "The real estate advisory practice — property transactions, leasing and investment guidance across Delhi.",
    },
    {
        name: "Kanwood Interiors",
        text: "The interior design and execution practice — turnkey projects across residential, commercial and institutional settings.",
    },
    {
        name: "Kavraj Atelier",
        text: "Our in-house furniture manufacturing division — bespoke residential pieces and project supply at scale.",
    },
];

const About = () => (
    <div data-testid="about-page">
        <Seo
            title="About Kavraj Spaces | Our Story & Legacy — New Delhi"
            description="Kavraj Spaces unites Marwaha Enterprises, Kanwood Interiors and Kavraj Atelier — a 60-year legacy across real estate, interiors and furniture in Delhi."
        />

        <PageHero
            eyebrow="About Kavraj Spaces"
            title="A shared history, brought under one name."
            intro="Kavraj Spaces brings together the expertise of Marwaha Enterprises, Kanwood Interiors, and an in-house furniture manufacturing division — offering clients a coordinated experience, from identifying the right property to designing, furnishing, and completing the space."
            testid="about-hero"
        />

        <section className="bg-cream" data-testid="about-founders">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-20 sm:px-12 lg:grid-cols-2 lg:gap-20 lg:px-20 lg:py-32">
                <Reveal>
                    <img
                        src={IMAGES.studio.url}
                        alt={IMAGES.studio.alt}
                        loading="lazy"
                        className="h-full w-full object-cover"
                    />
                </Reveal>
                <Reveal delay={120}>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                        The founders
                    </p>
                    <h2 className="mt-4 font-serif text-3xl tracking-tight text-ink sm:text-4xl">
                        Yuvraj Singh Marwaha & Kanav Bakshi
                    </h2>
                    <div className="mt-6 space-y-5 text-base leading-relaxed text-stone2">
                        <p>
                            Yuvraj and Kanav have been friends since middle school. They grew up
                            with shared business interests and family roots in real estate,
                            interiors and furniture — conversations about buildings, spaces and
                            craft were part of everyday life.
                        </p>
                        <p>
                            Their studies took them to different countries, and their early careers
                            took them into different corners of the same world: property on one side,
                            interiors and furniture on the other. Reconnecting in India through work,
                            they saw what clients already knew — that a property decision, an interior
                            and its furniture are really one decision.
                        </p>
                        <p>
                            Kavraj Spaces is the result: two complementary businesses, and the
                            friendships and family legacies behind them, brought together as a single
                            partner for the whole journey.
                        </p>
                    </div>
                    <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
                        {CONTACTS.filter((c) => c.id === "yuvraj" || c.id === "kanav").map((c) => (
                            <div
                                key={c.id}
                                data-testid={`founder-card-${c.id}`}
                                className="border border-bronze/20 bg-cream-muted p-6"
                            >
                                <h3 className="font-serif text-xl text-ink">{c.name}</h3>
                                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-bronze">
                                    {c.role}
                                </p>
                                <p className="mt-3 text-sm leading-relaxed text-stone2">{c.focus}</p>
                            </div>
                        ))}
                    </div>
                </Reveal>
            </div>
        </section>

        <section className="border-y border-bronze/15 bg-cream-muted" data-testid="about-pillars">
            <div className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20 lg:py-32">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                        What stands behind the name
                    </p>
                    <h2 className="mt-4 max-w-2xl font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        Three strengths, one standard.
                    </h2>
                </Reveal>
                <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
                    {PILLARS.map((pillar, i) => (
                        <Reveal key={pillar.name} delay={i * 120}>
                            <div className="h-full border border-bronze/20 bg-cream p-8" data-testid={`pillar-${i}`}>
                                <span className="font-serif text-sm italic text-bronze">0{i + 1}</span>
                                <h3 className="mt-3 font-serif text-2xl text-ink">{pillar.name}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-stone2">{pillar.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        <section className="bg-charcoal" data-testid="about-proof">
            <div className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20 lg:py-28">
                <Reveal>
                    <h2 className="max-w-2xl font-serif text-3xl tracking-tight text-cream sm:text-4xl">
                        A record built over decades.
                    </h2>
                </Reveal>
                <div className="mt-14 grid grid-cols-1 gap-12 sm:grid-cols-3">
                    {STATS.map((stat, i) => (
                        <Reveal key={stat.label} delay={i * 120}>
                            <div className="border-l-2 border-bronze pl-6" data-testid={`about-stat-${i}`}>
                                <p className="font-serif text-5xl tracking-tight text-cream lg:text-6xl">
                                    {stat.value}
                                </p>
                                <p className="mt-3 max-w-[16rem] text-sm leading-relaxed text-cream/70">
                                    {stat.label}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={200}>
                    <p className="mt-12 max-w-2xl text-sm leading-relaxed text-cream/60">
                        Our real estate practice has served over a hundred clients — including HNI and
                        UHNI advisory — and has sold property inventory valued at more than INR 600
                        crore.
                    </p>
                </Reveal>
            </div>
        </section>

        <CtaBand
            title="Work with a partner who knows the whole journey."
            text="Property, interiors, furniture — or all three together. Start with one conversation."
        />
    </div>
);

export default About;
